import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { Check, Loader2 } from 'lucide-react';
import { useToast } from '../shared/Toast';
import API_BASE_URL from '../config/api';
import { SwitchRow } from './instantSettings';
import { SettingsSheet, SheetBlock, SheetSection } from './sheet';
import { StoreLoadState } from './storeSettingsForm';

/**
 * Λειτουργίες → «Ανάθεση τραπεζιών»: ποιος σερβιτόρος βλέπει ποια τραπέζια (stores.table_responsibility_enabled,
 * migration 080). Ο διακόπτης αποθηκεύεται αμέσως· οι αναθέσεις ενός υπαλλήλου με το δικό του «Αποθήκευση».
 * Διάταξη «Φύλλο» (13/10/2026), χωρίς ευρετήριο: ο υπάλληλος διαλέγεται αριστερά (κάτω από την εξήγηση),
 * οι ζώνες και τα τραπέζια του δεξιά.
 */

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  role_name: string;
  role_display_name: string;
}

interface Zone {
  id: number;
  name: string;
  store_id: number;
}

interface TableItem {
  id: number;
  table_number: number;
  name: string;
  zone_id: number;
  zone_name: string;
}

interface Assignment {
  tables: number[];
  zones: number[];
}

const SUBHEAD = 'text-sm font-semibold text-t1';
const SUBHINT = 'mt-0.5 mb-3 text-xs text-t3';

const TableResponsibility: React.FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [tables, setTables] = useState<TableItem[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [assignments, setAssignments] = useState<Assignment>({ tables: [], zones: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);

  useEffect(() => {
    if (store?.id) {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('xentrix_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch store settings
      const storeRes = await fetch(`${API_BASE_URL}/api/stores/${store!.id}`, { headers });
      if (storeRes.ok) {
        const storeData = await storeRes.json();
        setEnabled(storeData.table_responsibility_enabled || false);
      }

      // Fetch employees (waiters, senior waiters, cashiers)
      const empRes = await fetch(`${API_BASE_URL}/api/stores/${store!.id}/employees`, { headers });
      if (empRes.ok) {
        const empData = await empRes.json();
        // Filter to only show relevant roles (dine-in staff)
        const relevantRoles = ['waiter', 'senior_waiter', 'cashier', 'manager'];
        setEmployees(empData.filter((e: Employee) => relevantRoles.includes(e.role_name)));
      }

      // Fetch zones
      const zonesRes = await fetch(`${API_BASE_URL}/api/dine-in/${store!.id}/zones`, { headers });
      if (zonesRes.ok) {
        const zonesData = await zonesRes.json();
        setZones(zonesData);
      }

      // Fetch tables
      const tablesRes = await fetch(`${API_BASE_URL}/api/dine-in/${store!.id}/tables`, { headers });
      if (tablesRes.ok) {
        const tablesData = await tablesRes.json();
        setTables(tablesData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeAssignments = async (employeeId: number) => {
    try {
      const token = localStorage.getItem('xentrix_token');
      const res = await fetch(
        `${API_BASE_URL}/api/dine-in/${store!.id}/employee-assignments/${employeeId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setAssignments({
          tables: data.tables.map((tb: any) => tb.table_id),
          zones: data.zones.map((z: any) => z.zone_id)
        });
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const handleEmployeeSelect = async (employee: Employee) => {
    setSelectedEmployee(employee);
    await fetchEmployeeAssignments(employee.id);
  };

  const toggleEnabled = async () => {
    try {
      setTogglingEnabled(true);
      const token = localStorage.getItem('xentrix_token');
      const res = await fetch(`${API_BASE_URL}/api/stores/${store!.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ table_responsibility_enabled: !enabled })
      });
      if (res.ok) {
        setEnabled(!enabled);
      } else {
        showToast('error', t('settings:tableAssign.toggleFailed'));
      }
    } catch (error) {
      console.error('Error toggling enabled:', error);
    } finally {
      setTogglingEnabled(false);
    }
  };

  const toggleZone = (zoneId: number) => {
    setAssignments(prev => ({
      ...prev,
      zones: prev.zones.includes(zoneId)
        ? prev.zones.filter(id => id !== zoneId)
        : [...prev.zones, zoneId]
    }));
  };

  const toggleTable = (tableId: number) => {
    setAssignments(prev => ({
      ...prev,
      tables: prev.tables.includes(tableId)
        ? prev.tables.filter(id => id !== tableId)
        : [...prev.tables, tableId]
    }));
  };

  const saveAssignments = async () => {
    if (!selectedEmployee) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('xentrix_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Save zone assignments
      const zoneRes = await fetch(
        `${API_BASE_URL}/api/dine-in/${store!.id}/employee-assignments/${selectedEmployee.id}/zones`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ zoneIds: assignments.zones })
        }
      );

      // Save table assignments
      const tableRes = await fetch(
        `${API_BASE_URL}/api/dine-in/${store!.id}/employee-assignments/${selectedEmployee.id}/tables`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ tableIds: assignments.tables })
        }
      );

      if (!zoneRes.ok || !tableRes.ok) {
        showToast('error', t('settings:tableAssign.saveFailed'));
      } else {
        showToast('success', t('settings:tableAssign.saved'));
      }
    } catch (error) {
      console.error('Error saving assignments:', error);
      showToast('error', t('settings:tableAssign.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Get total assigned tables count
  const getAssignedTablesCount = () => {
    const tablesFromZones = tables.filter(tb => assignments.zones.includes(tb.zone_id));
    const individualTables = tables.filter(tb => assignments.tables.includes(tb.id));
    const allAssigned = new Set([...tablesFromZones.map(tb => tb.id), ...individualTables.map(tb => tb.id)]);
    return allAssigned.size;
  };

  // Ανενεργή λειτουργία: υπάλληλοι και αναθέσεις αχνά και χωρίς κλικ — inert και για το πληκτρολόγιο.
  const off = !enabled;
  const offCls = off ? 'opacity-50 pointer-events-none' : '';

  const employeeList = (
    <div className={offCls} inert={off} data-testid="tables-employees">
      {employees.length === 0 ? (
        <p className="text-sm text-t3">{t('settings:tableAssign.noEmployees')}</p>
      ) : (
        <ul className="space-y-1" aria-label={t('settings:tableAssign.employees')}>
          {employees.map(emp => {
            const selected = selectedEmployee?.id === emp.id;
            return (
              <li key={emp.id}>
                <button
                  type="button"
                  aria-pressed={selected}
                  onClick={() => handleEmployeeSelect(emp)}
                  className={`w-full min-h-[44px] px-3 py-2 rounded-xl border text-left transition-colors ${
                    selected ? 'border-brand bg-brand-soft' : 'border-transparent hover:bg-surface-3'
                  }`}
                >
                  <span className={`block text-sm font-semibold truncate ${selected ? 'text-brand' : 'text-t1'}`}>
                    {emp.first_name} {emp.last_name}
                  </span>
                  <span className="block text-xs text-t3 truncate">{emp.role_display_name}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return (
    <SettingsSheet eyebrow={t('settings:nav.groups.operations')} title={t('settings:tableAssign.title')}
      subtitle={t('settings:tableAssign.subtitle')} index={false}>
      {loading ? (
        <SheetBlock><StoreLoadState status="loading" onRetry={fetchData} /></SheetBlock>
      ) : (
        <>
          <SheetSection id="tables-mode" title={t('settings:tableAssign.modeTitle')} hint={t('settings:tableAssign.modeHint')}>
            <SwitchRow
              title={t('settings:tableAssign.enableLabel')}
              subtitle={enabled ? t('settings:tableAssign.enabledDesc') : t('settings:tableAssign.disabledDesc')}
              checked={enabled}
              disabled={togglingEnabled}
              onChange={() => toggleEnabled()}
            />
            {off && <p className="xs-note">{t('settings:tableAssign.disabledNote')}</p>}
          </SheetSection>

          <SheetSection id="tables-assignments" title={t('settings:tableAssign.assignTitle')} hint={t('settings:tableAssign.assignHint')}
            aside={employeeList}>
            <div className={offCls} inert={off} data-testid="tables-assign-panel">
              {selectedEmployee ? (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[15px] font-semibold text-t1 truncate">
                        {selectedEmployee.first_name} {selectedEmployee.last_name}
                      </p>
                      <p className="text-sm text-t3">{t('settings:tableAssign.assignedCount', { count: getAssignedTablesCount() })}</p>
                    </div>
                    <button
                      type="button"
                      onClick={saveAssignments}
                      disabled={saving}
                      className="inline-flex items-center justify-center gap-2 min-h-[44px] px-5 rounded-xl bg-brand text-brand-fg hover:brightness-110 text-sm font-semibold transition disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Check className="w-4 h-4" aria-hidden="true" />}
                      {t('common:save')}
                    </button>
                  </div>

                  {/* Ανάθεση ανά ζώνη: όλα τα τραπέζια της ζώνης */}
                  <div className="mt-6">
                    <h3 className={SUBHEAD}>{t('settings:tableAssign.byZone')}</h3>
                    <p className={SUBHINT}>{t('settings:tableAssign.byZoneHint')}</p>
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-2">
                      {zones.map(zone => {
                        const zoneTableCount = tables.filter(tb => tb.zone_id === zone.id).length;
                        const isSelected = assignments.zones.includes(zone.id);
                        return (
                          <button
                            key={zone.id}
                            type="button"
                            aria-pressed={isSelected}
                            onClick={() => toggleZone(zone.id)}
                            className={`min-h-[44px] px-3 py-2.5 rounded-xl border text-left transition-colors ${
                              isSelected ? 'border-brand bg-brand-soft' : 'border-line-strong bg-surface-2 hover:bg-surface-3'
                            }`}
                          >
                            <span className="flex items-center justify-between gap-2">
                              <span className={`text-sm font-semibold truncate ${isSelected ? 'text-brand' : 'text-t1'}`}>{zone.name}</span>
                              {isSelected && <Check className="w-4 h-4 shrink-0 text-brand" aria-hidden="true" />}
                            </span>
                            <span className="block mt-0.5 text-xs text-t3">{t('settings:tableAssign.zoneTables', { count: zoneTableCount })}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Μεμονωμένα τραπέζια (όσα δεν καλύπτει ήδη επιλεγμένη ζώνη) */}
                  <div className="mt-6 pt-6 border-t border-line">
                    <h3 className={SUBHEAD}>{t('settings:tableAssign.byTable')}</h3>
                    <p className={SUBHINT}>{t('settings:tableAssign.byTableHint')}</p>

                    {zones.map(zone => {
                      const zoneTables = tables.filter(tb => tb.zone_id === zone.id);
                      const isZoneSelected = assignments.zones.includes(zone.id);

                      if (zoneTables.length === 0) return null;

                      return (
                        <div key={zone.id} className="mt-4 first-of-type:mt-0">
                          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-t2">
                            {zone.name}
                            {isZoneSelected && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-brand-soft text-brand">
                                {t('settings:tableAssign.wholeZone')}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-[repeat(auto-fill,minmax(3.25rem,1fr))] gap-2">
                            {zoneTables.map(table => {
                              const isSelected = assignments.tables.includes(table.id) || isZoneSelected;
                              return (
                                <button
                                  key={table.id}
                                  type="button"
                                  aria-pressed={isSelected}
                                  onClick={() => !isZoneSelected && toggleTable(table.id)}
                                  disabled={isZoneSelected}
                                  className={`min-h-[44px] rounded-xl border text-center text-sm font-semibold tabular-nums transition-colors ${
                                    isZoneSelected
                                      ? 'border-brand-line bg-brand-soft/60 text-brand cursor-not-allowed'
                                      : isSelected
                                        ? 'border-brand bg-brand-soft text-brand'
                                        : 'border-line-strong bg-surface-2 text-t1 hover:bg-surface-3'
                                  }`}
                                >
                                  {table.table_number}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p className="text-sm text-t3">{t('settings:tableAssign.pickEmployee')}</p>
              )}
            </div>
          </SheetSection>
        </>
      )}
    </SettingsSheet>
  );
};

export default TableResponsibility;
